import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConVersion } from '../../../api/cliente';
import { ErrorApi } from '../../../api/problema';
import type { CambiosCliente, Cliente, Propuesta } from '../../../api/ops/clientes';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Chip, type EstadoChip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { Icono } from '../../../componentes/Icono';
import { Ventana } from '../../../componentes/Ventana';
import { formularioDe, validar, valoresDeCampos, type DatosFormulario, type ErroresFormulario } from '../alta/datos';
import { Formulario } from '../alta/Formulario';
import type { ContextoClientes } from '../lista/contexto';
import { CANALES, IDIOMAS, PERMISOS, emailValido, erroresDeCampos, fechaCompleta, instagramLimpio, telefonoLegible, textoDeError } from '../lista/textos';

/** 412: el mismo texto que Negocio (1.14). */
const TEXTO_CONFLICTO = 'Alguien ha cambiado estos datos mientras tanto. Los he vuelto a cargar: revisa y guarda otra vez.';

/** Lo que sube «Datos» a la cabecera del panel: guardar los cambios (con «Deshacer» 5 s, G159). */
export type GuardarDatos = () => void;

const ESTADO_PROPUESTA: Readonly<Record<Propuesta['status'], [string, EstadoChip]>> = {
    sent: ['Enviada', 'pending'],
    accepted: ['Aceptada', 'confirmed'],
    rejected: ['Rechazada', 'problem'],
    expired: ['Caducada', 'problem'],
};

const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const diaMes = (iso: string) => {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : `${String(f.getDate())} ${MES_CORTO[f.getMonth()] ?? ''}`;
};

/** Los cambios de `updateCustomer` (solo lo que cambia) y las etiquetas que se añaden o quitan. */
function cambiosDe(antes: DatosFormulario, ahora: DatosFormulario, c: ContextoClientes): { cambios: CambiosCliente; poner: string[]; quitar: string[] } {
    const cambios: CambiosCliente = {};
    const texto = (v: string) => (v.trim() === '' ? null : v.trim());
    if (texto(ahora.nombre) !== texto(antes.nombre)) {
        cambios.local_display_name = texto(ahora.nombre);
    }
    if (texto(ahora.telefono) !== texto(antes.telefono)) {
        cambios.local_phone = ahora.telefono.trim() === '' ? null : ahora.telefono.replace(/[\s().-]/g, '');
    }
    if (texto(ahora.email) !== texto(antes.email)) {
        cambios.local_email = ahora.email.trim() === '' ? null : ahora.email.trim().toLowerCase();
    }
    if (instagramLimpio(ahora.instagram) !== instagramLimpio(antes.instagram)) {
        const ig = instagramLimpio(ahora.instagram);
        cambios.instagram_username = ig === '' ? null : ig;
    }
    if (ahora.idioma !== antes.idioma) {
        cambios.locale = ahora.idioma;
    }
    if (ahora.nacimiento !== antes.nacimiento) {
        cambios.birth_date = ahora.nacimiento === '' ? null : ahora.nacimiento;
        if (ahora.nacimiento !== '') {
            cambios.birth_date_consent = ahora.consentimientoNacimiento;
        }
    }
    const valores = valoresDeCampos(ahora, c.campos, antes.campos);
    if (Object.keys(valores).length > 0) {
        cambios.field_values = valores;
    }
    return { cambios, poner: ahora.etiquetas.filter((t) => !antes.etiquetas.includes(t)), quitar: antes.etiquetas.filter((t) => !ahora.etiquetas.includes(t)) };
}

function Linea({ etiqueta, candado = false, children }: { etiqueta: string; candado?: boolean; children: string }) {
    return (
        <div className="rv-cl-dato">
            <span>{etiqueta}</span>
            <strong className="rv-cl-cuenta">
                {candado && <Icono nombre="lock" />}
                {children}
            </strong>
        </div>
    );
}

/**
 * «Datos» de la ficha (2.3): con la cuenta vinculada, su nombre, teléfono y email en solo lectura con «Proponer una
 * corrección» (G164, G165); debajo, los datos de tu negocio, que edita quien tiene `clientes.fichas.editar` (Recepción y
 * Profesional solo los ven). Guardar sale a los 5 s salvo «Deshacer» (G159); con `If-Match`: si alguien cambió la ficha
 * antes (412), se recarga y se avisa; un contacto que ya es de otra ficha (409) marca su campo.
 */
export function Datos({ c, ficha, onFicha, onRecargar, onGuardar }: { c: ContextoClientes; ficha: ConVersion<Cliente>; onFicha: (f: ConVersion<Cliente>) => void; onRecargar: () => void; onGuardar: (g: GuardarDatos | null) => void }) {
    const { ops, negocio, permisos, avisar } = c;
    const cliente = ficha.datos;
    const inicial = useMemo(() => formularioDe(cliente), [cliente]);
    const [datos, setDatos] = useState<DatosFormulario>(inicial);
    const [intentado, setIntentado] = useState(false);
    const [deApi, setDeApi] = useState<ErroresFormulario>({});
    const [propuestas, setPropuestas] = useState<Propuesta[]>([]);
    const [corrigiendo, setCorrigiendo] = useState(false);
    const vinculada = cliente.status === 'linked' || cliente.account != null;
    const puedeEditar = permisos.puede(PERMISOS.editar) && !c.soloLectura && cliente.status !== 'anonymized';
    const puedeProponer = permisos.puede(PERMISOS.proponer) && !c.soloLectura && vinculada;
    const errores: ErroresFormulario = intentado ? { ...validar(datos, c.campos, false), ...deApi } : deApi;
    const { cambios, poner, quitar } = cambiosDe(inicial, datos, c);
    const hayCambios = Object.keys(cambios).length > 0 || poner.length > 0 || quitar.length > 0;

    useEffect(() => {
        if (!vinculada) {
            return;
        }
        let vivo = true;
        ops.listCorrectionProposals(negocio, cliente.id)
            .then((l) => {
                if (vivo) {
                    setPropuestas(l.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, cliente.id, vinculada]);

    const enviar = (antes: ConVersion<Cliente>, d: DatosFormulario, cambiosAhora: CambiosCliente, ponerAhora: string[], quitarAhora: string[]) => {
        const id = antes.datos.id;
        const cambio = Object.keys(cambiosAhora).length > 0 ? ops.updateCustomer(negocio, id, cambiosAhora, antes.version ?? '') : Promise.resolve(null);
        cambio
            .then(async (r) => {
                for (const t of ponerAhora) {
                    await ops.addCustomerTag(negocio, id, t);
                }
                for (const t of quitarAhora) {
                    await ops.removeCustomerTag(negocio, id, t);
                }
                const nueva = r !== null && ponerAhora.length === 0 && quitarAhora.length === 0 ? r : await ops.getCustomer(negocio, id);
                onFicha(nueva);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: TEXTO_CONFLICTO });
                    onRecargar();
                    return;
                }
                const campos = erroresDeCampos(e);
                const primero = Object.values(campos)[0];
                setDatos(d);
                setDeApi(campos);
                avisar({ tipo: 'error', texto: primero ?? textoDeError(e, 'No he podido guardar los cambios. Prueba de nuevo.') });
            });
    };

    const guardar = () => {
        setIntentado(true);
        setDeApi({});
        if (Object.keys(validar(datos, c.campos, false)).length > 0) {
            return;
        }
        const antes = ficha;
        const d = datos;
        const [cambiosAhora, ponerAhora, quitarAhora] = [cambios, poner, quitar];
        c.programar({
            texto: 'Cambios guardados en la ficha de tu negocio.',
            donde: `ficha:${cliente.id}`,
            enviar: () => {
                enviar(antes, d, cambiosAhora, ponerAhora, quitarAhora);
            },
            alDeshacer: () => {
                setDatos(formularioDe(antes.datos));
                setIntentado(false);
            },
        });
    };

    // «Guardar» en la cabecera del panel solo con algo cambiado.
    const ultimo = useRef(guardar);
    useEffect(() => {
        ultimo.current = guardar;
    });
    const llamar = useCallback(() => {
        ultimo.current();
    }, []);
    const hay = hayCambios && puedeEditar;
    useEffect(() => {
        onGuardar(hay ? llamar : null);
    }, [hay, onGuardar, llamar]);
    useEffect(
        () => () => {
            onGuardar(null);
        },
        [onGuardar],
    );

    const pendiente = c.pendiente !== null && c.pendiente.donde === `ficha:${cliente.id}` ? c.pendiente : null;
    const idioma = IDIOMAS.find((i) => i.valor === cliente.locale)?.texto ?? '—';

    return (
        <div className="rv-cl-seccion">
            {pendiente !== null && <Deshacer key={pendiente.clave} clave={pendiente.clave} texto={pendiente.texto} fondo="suave" duracion={c.duracion} movil={c.movil} onDeshacer={c.deshacer} />}

            {/* «Email no válido» y «Teléfono no válido» con su fecha (G325, G354): no se usa hasta que se corrija. */}
            {(
                [
                    ['Email no válido', cliente.email_undeliverable_at],
                    ['Teléfono no válido', cliente.phone_undeliverable_at],
                ] as const
            ).map(([texto, fecha]) =>
                fecha == null ? null : (
                    <div key={texto} className="rv-cl-dato" role="status">
                        <span className="rv-cl-cuenta">
                            <Icono nombre="triangle-alert" />
                            {texto}
                        </span>
                        <Chip estado="problem">{`Desde el ${fechaCompleta(fecha)}`}</Chip>
                    </div>
                ),
            )}

            {vinculada && cliente.account != null ? (
                <>
                    <h3 className="rv-cl-subtitulo">
                        <Icono nombre="circle-check" />
                        Su Cuenta Reservaria
                    </h3>
                    <Linea etiqueta="Nombre" candado>
                        {cliente.account.display_name}
                    </Linea>
                    <Linea etiqueta="Teléfono" candado>
                        {telefonoLegible(cliente.account.phone) || '—'}
                    </Linea>
                    <Linea etiqueta="Email" candado>
                        {cliente.account.email ?? '—'}
                    </Linea>
                    <div className="rv-cl-fila-nota">
                        <p className="rv-cl-nota">Solo los cambia el cliente, desde «Mis datos». Tu negocio no puede editarlos.</p>
                        {puedeProponer && (
                            <Boton
                                tipo="secundario"
                                icono="pencil"
                                movil={c.movil}
                                onClick={() => {
                                    setCorrigiendo(true);
                                }}
                            >
                                Proponer una corrección
                            </Boton>
                        )}
                    </div>
                    {propuestas.map((p) => (
                        <div key={p.id} className="rv-cl-dato">
                            <span className="rv-cl-linea">
                                <strong>{`${p.field === 'email' ? 'Email' : 'Nombre'}: «${p.proposed_value}»`}</strong>
                                <span>{p.status === 'sent' ? `Caduca el ${fechaCompleta(p.expires_at)}` : p.decided_at != null ? `Decidida el ${diaMes(p.decided_at)}` : `Caducó el ${diaMes(p.expires_at)}`}</span>
                            </span>
                            <Chip estado={ESTADO_PROPUESTA[p.status][1]}>{ESTADO_PROPUESTA[p.status][0]}</Chip>
                        </div>
                    ))}
                </>
            ) : (
                <div className="rv-cl-caja">
                    <span className="rv-cl-caja__titulo">
                        <Icono nombre="unlink" />
                        Sin vincular a una Cuenta Reservaria
                    </span>
                    <span>Sus datos son solo de tu negocio. Se vincula cuando el cliente verifica su teléfono contigo, al reservar o al identificarse con un código.</span>
                </div>
            )}

            {puedeEditar ? (
                <Formulario
                    datos={datos}
                    onCambio={(d) => {
                        setDatos(d);
                        setDeApi({});
                    }}
                    errores={errores}
                    campos={c.campos}
                    etiquetas={c.etiquetas}
                    alta={false}
                    vinculada={vinculada}
                />
            ) : (
                <>
                    <h3 className="rv-cl-subtitulo">Datos de tu negocio</h3>
                    <Linea etiqueta={vinculada ? 'Nombre en tu negocio' : 'Nombre y apellidos'}>{cliente.local.display_name ?? '—'}</Linea>
                    <Linea etiqueta="Teléfono">{telefonoLegible(cliente.local.phone) || '—'}</Linea>
                    <Linea etiqueta="Email">{cliente.local.email ?? '—'}</Linea>
                    <Linea etiqueta="Instagram">{cliente.local.instagram_username == null ? '—' : `@${cliente.local.instagram_username}`}</Linea>
                    <Linea etiqueta="Idioma de sus mensajes">{idioma}</Linea>
                    <Linea etiqueta="Fecha de nacimiento">{fechaCompleta(cliente.birth_date) || '—'}</Linea>
                    {c.campos
                        .filter((x) => x.archived_at == null)
                        .map((x) => {
                            const v = cliente.field_values?.[x.key];
                            const t = x.type === 'boolean' ? (v === true ? 'Sí' : 'No') : Array.isArray(v) ? v.join(', ') || '—' : x.type === 'date' && typeof v === 'string' ? fechaCompleta(v) || '—' : typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : '—';
                            return (
                                <Linea key={x.id} etiqueta={x.label}>
                                    {t}
                                </Linea>
                            );
                        })}
                    <Linea etiqueta="Etiquetas">
                        {(cliente.tag_ids ?? [])
                            .map((t) => c.etiquetas.find((x) => x.id === t)?.name)
                            .filter((n): n is string => n !== undefined)
                            .join(', ') || 'Sin etiquetas'}
                    </Linea>
                    <p className="rv-cl-nota">{c.soloLectura ? 'El negocio está en solo lectura: puedes consultar, pero no cambiar nada.' : 'Con tu rol puedes ver estos datos, pero no cambiarlos.'}</p>
                </>
            )}

            {corrigiendo && cliente.account != null && (
                <VentanaCorreccion
                    c={c}
                    cliente={cliente}
                    propuestas={propuestas}
                    onCerrar={() => {
                        setCorrigiendo(false);
                    }}
                    onEnviada={(p) => {
                        setCorrigiendo(false);
                        setPropuestas((l) => [p, ...l]);
                    }}
                />
            )}
        </div>
    );
}

/** «Proponer una corrección» (2.3, G164): nombre o email de la cuenta; la acepta o no el cliente en «Mis datos». */
function VentanaCorreccion({ c, cliente, propuestas, onCerrar, onEnviada }: { c: ContextoClientes; cliente: Cliente; propuestas: readonly Propuesta[]; onCerrar: () => void; onEnviada: (p: Propuesta) => void }) {
    const abiertas = (campo: 'display_name' | 'email') => propuestas.some((p) => p.field === campo && p.status === 'sent');
    const [campo, setCampo] = useState<'display_name' | 'email'>(abiertas('display_name') && !abiertas('email') ? 'email' : 'display_name');
    const [valor, setValor] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const actual = campo === 'email' ? (cliente.account?.email ?? '') : (cliente.account?.display_name ?? '');
    const via = cliente.preferred_channel == null ? 'su canal preferido' : CANALES[cliente.preferred_channel].nombre;
    const v = valor.trim();
    const errorCampo = abiertas(campo) ? 'Ya hay una propuesta enviada para este dato.' : '';
    const errorValor = v === '' ? 'Escribe el dato correcto.' : campo === 'email' && !emailValido(v) ? 'Escribe un email válido.' : v.toLocaleLowerCase('es') === actual.toLocaleLowerCase('es') ? 'Es el mismo dato que tiene ahora.' : '';

    const enviar = () => {
        setIntentado(true);
        if (errorCampo !== '' || errorValor !== '') {
            return;
        }
        setOcupado(true);
        c.ops
            .createCorrectionProposal(c.negocio, cliente.id, { field: campo, proposed_value: v })
            .then((p) => {
                c.avisar({ tipo: 'ok', texto: `Propuesta enviada por ${via}. Si no responde, caduca el ${fechaCompleta(p.expires_at)}.` });
                onEnviada(p);
            })
            .catch((e: unknown) => {
                c.avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido enviar la propuesta. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <Ventana
            abierta
            titulo="Proponer una corrección"
            texto={`Le enviamos un enlace por ${via} para que la acepte o no desde «Mis datos». Caduca a los 30 días; mientras tanto ves el dato de tu negocio.`}
            accion={c.movil ? 'Enviar' : 'Enviar la propuesta'}
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Enviando…"
            movil={c.movil}
            onAccion={enviar}
            onCerrar={onCerrar}
        >
            <div className="rv-cl-ventana-campos">
                <Desplegable
                    etiqueta="Dato"
                    valor={campo}
                    opciones={[
                        { valor: 'display_name', texto: 'Nombre' },
                        { valor: 'email', texto: 'Email' },
                    ]}
                    onChange={(x) => {
                        setCampo(x === 'email' ? 'email' : 'display_name');
                    }}
                    error={intentado ? errorCampo : ''}
                />
                <Campo
                    etiqueta={campo === 'email' ? 'Email correcto' : 'Nombre correcto'}
                    tipo={campo === 'email' ? 'email' : 'text'}
                    valor={valor}
                    onChange={setValor}
                    error={intentado ? errorValor : ''}
                    ayuda={`Ahora: ${actual === '' ? '—' : actual}`}
                    autocompletar="off"
                    max={254}
                    onEnter={enviar}
                />
            </div>
        </Ventana>
    );
}
